import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axiosWithAuth from "../../utils/axiosWithAuth";
import axios from "axios";
import { getApiEndpoint } from "../../utils/apiConfig";
import Header from "../../components/Header";
import { SquareLoader } from "react-spinners";
import './RepairSummary.scss';

const RepairSummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  let { id } = useParams();
  
  // Parse URL parameters
  const queryParams = new URLSearchParams(location.search);
  const queryId = queryParams.get('id');
  
  // Use ID from params or query
  id = id || queryId;
  
  document.title = t("maintenance.0");
  const headerTitle = t("maintenance.0");
  const backwardNavigate = "/material";
  
  // State variables
  const [isLoading, setIsLoading] = useState(true);
  const [materialPrice, setMaterialPrice] = useState({});
  const [breakdown, setBreakdown] = useState(null);
  const [isTemporary, setIsTemporary] = useState(false);
  const [error, setError] = useState(null);
  
  // Get property and home data from session storage
  const selectedPropertyType = JSON.parse(sessionStorage.getItem("selectedPropertyType") || "{}");
  const selectedHomeData = JSON.parse(sessionStorage.getItem("homeData") || "{}");
  const selectedRooms = JSON.parse(sessionStorage.getItem("roomValues") || "[]");
  
  // Calculate total room area
  const calculateTotalRoomArea = useCallback(() => {
    if (breakdown?.rooms && breakdown.rooms.length > 0) {
      return breakdown.rooms.reduce((total, room) => {
        return total + (parseFloat(room.area) || 0);
      }, 0);
    }
    
    if (selectedRooms && selectedRooms.length > 0) {
      return selectedRooms.reduce((total, room) => {
        return total + (parseFloat(room.area) || 0);
      }, 0);
    }
    
    return 0;
  }, [breakdown, selectedRooms]);
  
  // Load repair data
  useEffect(() => {
    const loadRepairData = async () => {
      try {
        setIsLoading(true);
        
        // Check if ID is a breakdown ID
        if (id && id.startsWith('breakdown-')) {
          const breakdownId = id.replace('breakdown-', '');
          console.log("Loading breakdown data for ID:", breakdownId);
          
          const { getBreakdown } = await import('../../services/repairService');
          const response = await getBreakdown(breakdownId);
          
          if (response.success && response.data) {
            const breakdownData = response.data;
            const breakdownDetails = breakdownData.breakdown || breakdownData;
            const costs = breakdownData.costs || {};
            
            setBreakdown(breakdownDetails);
            setMaterialPrice({
              materialsCost: costs.materialsCost || 0,
              serviceCost: costs.serviceCost || 0,
              generalCost: costs.generalCost || 0,
              currency: costs.currency || "AZN",
              userInfo: breakdownData.userProfile?.userName || t("maintenance.anonymousUser"),
              isBreakdown: true,
              isTemporary: false
            });
          } else {
            throw new Error('Failed to load breakdown data');
          }
        } else {
          // Handle regular repair data (temp or current)
          const repairData = id?.startsWith('temp-') 
            ? JSON.parse(localStorage.getItem(id) || '{}')
            : JSON.parse(sessionStorage.getItem('currentRepairData') || '{}');

          if (!repairData || Object.keys(repairData).length === 0) {
            console.error('No repair data found');
            navigate('/dimensionroom');
            return;
          }
          
          // Check if we have existing costs
          if (repairData.costs) {
            setMaterialPrice({
              materialsCost: repairData.costs.materialsCost || 0,
              serviceCost: repairData.costs.serviceCost || 0,
              generalCost: repairData.costs.generalCost || 0,
              currency: repairData.costs.currency || "AZN",
              userInfo: repairData.user?.userName || t("maintenance.anonymousUser"),
              isTemporary: id?.startsWith('temp-') || false
            });
            
            if (repairData.breakdown) {
              setBreakdown(repairData.breakdown);
            }
          } else {
            // If no costs, we need to calculate them
            console.log('No costs found in repair data, calculating...');
            
            // Try to get costs from API if user is logged in
            const token = localStorage.getItem('token');
            if (token) {
              try {
                const response = await axiosWithAuth().post(
                  getApiEndpoint('repairs/calculate-breakdown'), 
                  repairData
                );
                
                if (response.data && response.data.success) {
                  const costs = response.data.costs || {};
                  const breakdownData = response.data.breakdown || response.data.data;
                  
                  setMaterialPrice({
                    materialsCost: costs.materialsCost || 0,
                    serviceCost: costs.serviceCost || 0,
                    generalCost: costs.generalCost || 0,
                    currency: costs.currency || "AZN",
                    userInfo: repairData.user?.userName || t("maintenance.anonymousUser"),
                    isTemporary: id?.startsWith('temp-') || false
                  });
                  
                  setBreakdown(breakdownData);
                } else {
                  throw new Error('API calculation failed');
                }
              } catch (apiError) {
                console.error('API calculation failed:', apiError);
                // Use fallback values
                setMaterialPrice({
                  materialsCost: 0,
                  serviceCost: 0,
                  generalCost: 0,
                  currency: "AZN",
                  userInfo: repairData.user?.userName || t("maintenance.anonymousUser"),
                  isTemporary: id?.startsWith('temp-') || false
                });
              }
            } else {
              // No token, use fallback values
              setMaterialPrice({
                materialsCost: 0,
                serviceCost: 0,
                generalCost: 0,
                currency: "AZN",
                userInfo: t("maintenance.anonymousUser"),
                isTemporary: id?.startsWith('temp-') || false
              });
            }
          }
          
          setIsTemporary(id?.startsWith('temp-') || false);
        }
      } catch (error) {
        console.error("Error loading repair data:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadRepairData();
  }, [id, navigate, t]);
  
  // Handle back button click
  const handleBackwardNavigation = useCallback(() => {
    navigate(backwardNavigate);
  }, [navigate, backwardNavigate]);
  
  // Handle detailed calculation button click
  const handleDetailedCalculation = () => {
    const token = localStorage.getItem('token');
    
    // Force authentication check for detailed calculation - ignore skipLoginCheck
    if (!token) {
      console.log("Authentication required for detailed calculation");
      
      // Store current repair data and path for redirect after login
      const currentPath = window.location.pathname + window.location.search;
      localStorage.setItem('redirectAfterLogin', `/maintenance/${id}`);
      
      // If it's a temp repair, store the ID for later use
      if (id?.startsWith('temp-')) {
        localStorage.setItem('pendingRepairId', id);
      }
      
      // Show alert to user about login requirement
      alert(t("maintenance.loginRequiredForDetails") || "Ətraflı hesablama məlumatlarını görmək üçün giriş etməlisiniz");
      
      // Redirect to login
      navigate('/login', { 
        state: { 
          returnUrl: `/maintenance/${id}`,
          message: t("maintenance.loginRequiredForDetails") || "Ətraflı hesablama məlumatlarını görmək üçün giriş etməlisiniz"
        } 
      });
      return;
    }
    
    // User is authenticated, proceed to maintenance page
    navigate(`/maintenance/${id}`);
  };
  
  // Handle go to profile button click
  const handleGoToProfile = () => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/account');
    } else {
      // If not logged in, redirect to login with return URL
      const currentPath = window.location.pathname;
      navigate('/login', { state: { returnUrl: currentPath } });
    }
  };
  
  if (isLoading) {
    return (
      <div className="repair-summary-container">
        <Header
          title={headerTitle}
          showProfileIcon={false}
          showBackIcon={true}
          onBackClick={handleBackwardNavigation}
        />
        <div className="loader-container">
          <SquareLoader color="#cda742" size={80} />
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="repair-summary-container">
        <Header
          title={headerTitle}
          showProfileIcon={false}
          showBackIcon={true}
          onBackClick={handleBackwardNavigation}
        />
        <div className="container">
          <div className="alert alert-danger">
            <h5>Xəta baş verdi</h5>
            <p>{error}</p>
            <button 
              className="btn btn-primary mt-2" 
              onClick={() => navigate('/dimensionroom')}
            >
              Yenidən başla
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="repair-summary-container">
      <Header
        title={headerTitle}
        showProfileIcon={true}
        showBackIcon={true}
        onBackClick={handleBackwardNavigation}
      />
      
      <div className="container repair-summary-content">
        {/* Property Information */}
        {selectedPropertyType && selectedHomeData && (
          <div className="maintenance-wrap">
            <div className="maintenance-info">
              <div className="maintenance-infoDiv">
                <span className="maintenance-label">{t("maintenance.propertyType")}:</span>
                <span className="maintenance-price">{selectedPropertyType.name?.text || selectedPropertyType.name?.az || 'N/A'}</span>
              </div>
              <div className="maintenance-infoDiv">
                <span className="maintenance-label">{t("maintenance.givenArea")}:</span>
                <span className="maintenance-price">{selectedHomeData.areaHouse || 0} m²</span>
              </div>
              <div className="maintenance-infoDiv">
                <span className="maintenance-label">{t("maintenance.calculatedArea")}:</span>
                <span className="maintenance-price">{calculateTotalRoomArea().toFixed(2)} m²</span>
              </div>
              <div className="maintenance-infoDiv">
                <span className="maintenance-label">{t("calculator.3")}:</span>
                <span className="maintenance-price">{selectedHomeData.heightWall || 0} m</span>
              </div>
            </div>
          </div>
        )}
        
        {/* Cost Summary */}
        <div className="maintenance-section mb-3">
          <div className="maintenance-item total-item">
            <span>{t("maintenance.materials")}</span>
            <div>
              <div className="total-price">{(materialPrice.materialsCost || 0).toFixed(2)} {materialPrice.currency || "AZN"}</div>
            </div>
          </div>
          <div className="maintenance-item total-item">
            <span>{t("maintenance.service")}</span>
            <div>
              <div className="total-price">{(materialPrice.serviceCost || 0).toFixed(2)} {materialPrice.currency || "AZN"}</div>
            </div>
          </div>
          <div className="maintenance-item total-item">
            <span>{t("maintenance.total")}</span>
            <div>
              <div className="total-price">{(materialPrice.generalCost || 0).toFixed(2)} {materialPrice.currency || "AZN"}</div>
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="repair-summary-buttons">
          <button 
            className="btn py-3 global-button mb-3" 
            onClick={handleDetailedCalculation}
          >
            {t("maintenance.detailedCalculation")}
          </button>
          <button 
            className="btn py-3 global-button-secondary" 
            onClick={handleGoToProfile}
          >
            {t("maintenance.goToProfile")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RepairSummary; 